<template>
  <a id="ProductFilterFunnel" />
  <div v-editable="blok" class="ProductFilterFunnel">
    <!-- * * * * acual funnel -->
    <div
      v-if="!showingResult"
      class="layout-container"
      :class="[desktopWidth('full'), blockPaddingTop('md'), blockPaddingBottom('md')]"
    >
      <!-- * * * * Heading * * * * -->
      <div class="flex flex-col justify-center items-center gap-8 desk:gap-12 desktopMaxW-sm">
        <div class="type-headline-2xl text-center">{{ blok.headline }}</div>
        <div class="type-md text-text-subtle text-center" v-html="renderRichText(blok.description)" />
      </div>

      <!-- * * * * Wrapper * * * * -->
      <div v-if="facetOrder.length" class="grid grid-cols-1 desk:grid-cols-12 gap-32 mt-64">
        <div
          class="col-span-1 desk:col-span-7 self-start p-16 desk:p-32 bg-bg-base border border-border-subtle rounded-2"
        >
          <!-- * * * * Progress Bar (Global) * * * * -->
          <div class="">
            <div class="flex items-center justify-between mb-8">
              <div class="type-sm">
                {{ $t('floorFinder.step', { currentStep: currentStep + 1, totalSteps }) }}
              </div>
              <div class="flex divide-x divide-border-base">
                <button
                  class="inline-flex items-center gap-8 pr-8 type-sm"
                  @click="reset"
                >
                  <img
                    class="w-12 h-12 desk:w-16 desk:h-16"
                    src="/icons/refresh.svg"
                    :alt="$t('floorFinder.reset')"
                  >
                  {{ $t('floorFinder.reset') }}
                </button>

                <div class="pl-8 type-sm cursor-pointer" @click="showResult">
                  <LoadingIcon v-if="initalLoading || loading" />
                  <span v-else>{{ currentProductCount }}</span> {{ $t('floorFinder.floorsFound') }}
                </div>
              </div>
            </div>

            <div class="w-full h-4 bg-bg-inactive rounded-full">
              <div
                class="bg-bg-successStrong h-4 rounded-full transition-all duration-300"
                :style="{ width: `${100 / totalSteps * (currentStep + 1)}%` }"
              />
            </div>

            <!-- * * * * Guide Panel * * * * -->
            <div class="mt-24">
              <!-- Title and description -->
              <div class="flex flex-col gap-4 desk:gap-8 mb-20 desk:mb-24">
                <div class="type-headline-xl">{{ stepTitle }}</div>
                <div class="type-base text-text-subtle empty:hidden">{{ thisStepContent.description }}</div>
              </div>

              <!-- Filters -->
              <div v-if="initalLoading" class="mt-32 flex justify-center">
                <LoadingIcon size="lg" />
              </div>
              <div v-else>
                <!-- Filters -->
                <div class="grid grid-cols-2 desk:grid-cols-3 gap-8 desk:gap-12 mb-24 desk:mb-32">
                  <div
                    v-for="(item) in thisStepFacets"
                    :key="`${currentStepCode}.${item.code}`"
                    class="relative rounded-2 overflow-hidden"
                  >
                    <label
                      class="flex justify-end items-end gap-8 aspect-landscape relative cursor-pointer hover:opacity-80 transition ease-out duration-500"
                    >
                      <nuxt-img
                        v-if="getFacetData(currentStepCode, item.code )?.image?.filename"
                        preset="standard"
                        sizes="sm:100vw desk:233px"
                        class="absolute h-full w-full object-cover"
                        :src="getFacetData(currentStepCode, item.code )?.image?.filename"
                        :alt="getFacetData(currentStepCode, item.code )?.image?.alt"
                      />
                      <img
                        v-else
                        src="/icons/imageMissing.svg"
                        class="absolute h-full w-full object-cover"
                      >

                      <span
                        class="inputOverlay flex justify-start items-center gap-8 px-8 desk:px-16 pb-8 desk:pb-16 pt-40 desk:pt-56 w-full relative"
                      >
                        <input
                          type="checkbox"
                          class="relative min-w-20 min-h-20"
                          :checked="checkChecked(item.code)"
                          @click="(e) => switchFilter(e, item.code)"
                        >
                        <span class="type-headline-base text-text-inverse">
                          {{ item.name }}
                        </span>
                      </span>
                    </label>
                    <div
                      v-if="getFacetData(currentStepCode, item.code )?.tooltip"
                      class="absolute top-8 right-8 z-20"
                    >
                      <Tooltip :text="getFacetData(currentStepCode, item.code )?.tooltip" :show-on-hover="true">
                        <div class="inline-flex items-center justify-center w-24 h-24 bg-bg-base rounded-full">
                          <img
                            class="w-12 h-12 desk:w-16 desk:h-16"
                            src="/icons/info.svg"
                            alt="Info"
                          >
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Navigation Buttons (Global) -->
              <div v-if="!initalLoading" class="flex gap-8 mt-16">
                <!-- Previous Button -->
                <button
                  v-if="currentStep > 0"
                  class="btn btn--secondary btn--lg"
                  :disabled="currentStep === 0"
                  @click="goToPreviousStep"
                >
                  {{ $t('floorFinder.previous') }}
                </button>

                <!-- Next Button -->
                <button
                  v-if="currentStep < totalSteps - 1 && hasNextStep"
                  class="btn btn--lg grow"
                  :disabled="currentStep === totalSteps - 1 || loading"
                  @click="goToNextStep"
                >
                  {{ $t('floorFinder.next') }}
                </button>

                <!-- Complete Button -->
                <button
                  v-else
                  class="btn btn--lg grow"
                  @click="showResult"
                >
                  {{ $t('floorFinder.complete') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- * * * * Gallery * * * * -->
        <div v-if="!uiStore.isMobile" class="col-span-5 aspect-portrait relative rounded-2 overflow-hidden">
          <div
            class="w-full h-full absolute top-0 left-0 transition-opacity duration-300 ease-in-out"
          >
            <nuxt-img
              v-if="thisStepContent.image && thisStepContent.image.filename"
              preset="standard"
              sizes="sm:100vw desk:1260px"
              class="w-full h-full object-cover"
              :class="thisStepContent.focalPoint"
              :src="thisStepContent.image.filename"
              :alt="thisStepContent.image.alt"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-else
      class="bg-bg-base"
      :class="[blockPaddingTop('md'), blockPaddingBottom('md')]"
    >
      <div class="layout-container">
        <!-- * * * * Heading * * * * -->
        <div class="flex flex-col justify-center items-center gap-8 desk:gap-12 desktopMaxW-sm mb-64">
          <div class="type-headline-2xl text-center">{{ blok.headlineResults }}</div>
          <div class="type-md text-text-subtle text-center mb-16" v-html="renderRichText(blok.descriptionResults)" />
          <div class="flex gap-12">
            <button
              v-if="!props.blok.inline"
              class="btn btn--lg"
              @click="showEmailBox = true"
            >
              {{ $t('floorFinder.mailMe') }} <img
                class="w-20 h-20 ml-8 relative top-1"
                src="/icons/mail-inv.svg"
                :alt="$t('floorFinder.mailMe')"
              >
            </button>
            <button class="btn btn--outline btn--lg" @click="reset">
              {{ $t('floorFinder.reset') }} <img
                class="w-20 h-20 ml-8 relative top-1"
                src="/icons/refresh.svg"
                :alt="$t('floorFinder.reset')"
              >
            </button>
          </div>
        </div>
        <ProductList
          :blok="blokForProductList"
          :placement="1"
          :always-check-url="true"
        />
      </div>
      <ConfirmModal
        v-if="showEmailBox"
        :show-abort="false"
        :show-confirm="false"
        @click-outside="showEmailBox = false"
      >
        <div v-if="sendDone" class="type-headline-base">{{ $t('floorFinder.mailed') }}</div>
        <div v-else>
          <div class="type-headline-base">{{ $t('floorFinder.mailMe.text') }}</div>
          <Form
            class="mt-12"
            @submit="submitForm"
            @click="startValidate(true)"
          >
            <TextInput
              v-model="sendFirstName"
              type="text"
              autocomplete="given-name"
              :label="$t('form.placeholder.firstName')"
              class="mb-12"
            />
            <TextInput
              v-model="sendLastName"
              type="text"
              autocomplete="family-name"
              :label="$t('form.placeholder.lastName')"
              class="mb-12"
            />

            <Field
              v-slot="{ field, meta }"
              name="ContactEmail"
              :rules="validateEmail"
              :model-value="sendEmail"
            >
              <TextInput
                v-model="sendEmail"
                server-name="required"
                v-bind="field"
                type="email"
                :label="$t('form.placeholder.email')+' ('+$t('form.required')+')'"
                :show-error="meta.validated && !meta.valid"
                :errors="meta.errors"
                autocomplete="email"
              />
            </Field>

            <label class="block mt-12">
              <input
                v-model="acceptContact"
                type="checkbox"
                class="relative top-4"
              >
              <span v-html="$t('floorFinder.acceptContact')" />
            </label>

            <button
              class="btn mt-12"
              :class="{ loading: sending}"
            >
              {{ $t('form.send') }}
            </button>
          </Form>
        </div>
      </ConfirmModal>
    </div>

    <!-- * * * * content below result funnel -->
    <div v-if="isInStoryblokAdmin() && blok.contentBelowResult.length && !showingResult" class="py-64">
      Preview of result page content (shown in storyblok and on result page only):
    </div>
    <template v-if="showingResult || isInStoryblokAdmin()">
      <SpaceHandler v-for="subblok in blok.contentBelowResult" :blok="subblok">
        <component :is="subblok.component" :blok="subblok" />
      </SpaceHandler>
    </template>
  </div>
</template>

<script setup lang="ts">
import useAlgoliaFetch from '~/composeables/useAlgoliaFetch';
import { AlgoliaFacets, AlgoliaSearchPayloadRequestOptions, UserFilters } from '~/constants/types/algolia';
import { useGlobalContentStore } from '~/store/globalContent';
import Tooltip from '~/components/body/Tooltip.vue';
import { useUiStore } from '~/store/ui';
import ProductList from '~/components/product/ProductList.vue';
import {
  FacetGroupWithImage, ProductFilterSettings,
  StoryBlokPropBase
} from '~/constants/types/storyblok';
import { ISbRichtext } from 'storyblok-js-client';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import useStoryblokAdmin from '~/composeables/useStoryblokAdmin';
import SpaceHandler from '~/components/body/SpaceHandler.vue';
import ConfirmModal from '~/components/modals/ConfirmModal.vue';
import { Field, Form } from 'vee-validate';
import TextInput from '~/components/form-elements/TextInput.vue';
import useValidation from '~/composeables/useValidation';
import useApiFetch from '~/composeables/useApiFetch';

const globalContent = useGlobalContentStore();
const uiStore = useUiStore();
const { getFromAlgolia } = useAlgoliaFetch();
const storyblokApi = useStoryblokApi();
const { isInStoryblokAdmin } = useStoryblokAdmin();

type Props = StoryBlokPropBase<'ProductFilterFunnel'> & {
  headline: string,
  description: ISbRichtext,
  headlineResults: string,
  descriptionResults: ISbRichtext,
  facets: FacetGroupWithImage[],
  contentBelowResult: any[],
  inline: boolean
}

const props = defineProps<{
  blok: Props,
}>();
const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);

const initalLoading = ref(true);
const loading = ref(false);
const currentStep = ref(0);
const maxFacetsPerStep = 9;
//const facetOrder.value = ['parametricFilters.ProductColourCode', 'parametricFilters.ProductWoodSpecies', 'parametricFilters.ProductGrading'];

const facetOrder = computed(()=> {
  return props.blok.facets.map(m => m.norceCode);
});

const userFilters = ref<{ key: string, filters: string[]}[]>([]);

const totalSteps = computed(()=> facetOrder.value.length);

const currentStepCode = computed(()=> {
  return facetOrder.value[currentStep.value] || '';
});

const stepTitle = computed(()=> {
  if (thisStepContent.value.overrideName !== '') {
    return thisStepContent.value.overrideName;
  }
  return globalContent.getFilterName(currentStepCode.value);
});

const stepExcluded = computed(() => {
  return thisStepContent.value.excludeFacets?.split(',') || [];
});

const nextStepExcluded = computed(()=> {
  const nextStepCode = facetOrder.value[currentStep.value + 1];
  if (!nextStepCode || !currentFacets.value[nextStepCode]) {
    return [];
  }
  return props.blok.facets.find(f => f.norceCode === nextStepCode)?.excludeFacets?.split(',') || [];
});

const facetFilters = computed(()=> {
  // Selects floors only
  /*const output = [
    ['categoryFilters:-Samples'],
    ['categoryFilters:-Accessories'],
  ];*/

  const output = [
    ['categoryFilters:Floors'],
  ];

  userFilters.value.filter(f => f.filters.length).forEach(userFacet => {
    const thisGroup = userFacet.filters.map(m => `${userFacet.key}:${m}`);
    output.push(thisGroup);
  });

  return output;
});

const currentFacets = ref<AlgoliaFacets>({});
const currentProductCount = ref(0);

const storyblokSettings = ref<ProductFilterSettings | null>(null);
const storyblokLoaded = ref(false);

const loadFacets = async() => {
  // to not crash storyblok
  if (!facetOrder.value.length) {
    return;
  }

  loading.value = true;
  const query = {
    facetFilters: facetFilters.value,
    facets: facetOrder.value,
    hitsPerPage: 1,
    facetingAfterDistinct: true,
  } as AlgoliaSearchPayloadRequestOptions;

  const res = await getFromAlgolia(query, globalContent.getAlgoliaIndex);
  let res2;
  if (!storyblokLoaded.value) {
    res2 = await storyblokApi.get(`cdn/stories/${globalContent.getConsumerTypeSettingsPath}sitesettings/product-filter-settings`, {
      version: useRuntimeConfig().public.storyblokStoriesVersion as 'draft' | 'published',
    }).catch(() => {
      console.error('Couldn\'t load product-filter-settings from storyblok');
    });
  }

  if (res) {
    if (initalLoading.value) {
      currentFacets.value = res.facets;
    } else {
      // we only do changes to steps after the current steps, or the filter will only show what user just clicked
      facetOrder.value.forEach((facet, index) => {
        if (index > currentStep.value) {
          currentFacets.value[facet] = res.facets[facet];
        }
      });
    }
    currentProductCount.value = res.nbHits;
  } else {
    console.error('Couldn\'t load from algolia');
  }
  if (!storyblokLoaded.value && res2?.data?.story?.content) {
    storyblokSettings.value = res2.data?.story?.content;
  }

  loading.value = false;
  initalLoading.value = false;
  storyblokLoaded.value = true;
};

onMounted(()=> {
  setTimeout(() => {
    loadFacets();
  }, 50);
});

watch (facetFilters, () => loadFacets());

watch(() => props.blok.facets, () => {
  if (isInStoryblokAdmin()) {
    loadFacets();
  }
}, { deep: true });

const thisStepFacets = computed(()=> {
  const facetName = facetOrder.value[currentStep.value] || '';
  if (currentFacets.value[facetName]) {
    return Object.entries(currentFacets.value[currentStepCode.value])
      .map(([ name, count ]) => {
        return {
          name: globalContent.getFilterOption(`${currentStepCode.value}.${name}`).name,
          code: name,
          count,
        };
      })
      .filter((f) => {
        return !stepExcluded.value.some(s => s === f.code);
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, maxFacetsPerStep)
      .sort((a, b) => a.name.localeCompare(b.name));
  } else {
    console.error(`No loaded step named: "${currentStepCode.value}"`);
  }
});

const thisStepContent = computed(()=> {
  const sbContent = props.blok.facets.find(f => f.norceCode === currentStepCode.value);
  if (sbContent) {
    return {
      image: sbContent.image,
      description: sbContent.description || '',
      focalPoint: sbContent.focalPoint,
      overrideName: sbContent.overrideName,
      excludeFacets: sbContent.excludeFacets,
    };
  }
  return {
    image: null,
    description: '',
    focalPoint: '',
    overrideName: '',
  };
});

//  * * * * Steps

// Check if next steps has any facets, or it will be pointless to show them
const hasNextStep = computed(()=> {
  const facetName = facetOrder.value[currentStep.value + 1];
  if (!facetName || !currentFacets.value[facetName]) {
    return false;
  }
  return Object.entries(currentFacets.value[facetName])
    .map(([ name ]) => {
      return {
        code: name,
      };
    })
    .filter((f) => {
      return !nextStepExcluded.value.some(s => {
        return s === f.code;
      });
    })
    .length >= 2;
});

const goToPreviousStep = () => {
  currentStep.value--;

  // Resets userfilter for steps after current
  facetOrder.value.forEach((facet, index) => {
    if (index > currentStep.value) {
      const nextUserFilters = userFilters.value.findIndex(f => f.key === facet);
      if (nextUserFilters > -1) {
        userFilters.value[nextUserFilters].filters = [];
      }
    }
  });
};
const goToNextStep = () => {
  currentStep.value++;
};

const switchFilter = (e: Event, value: string) => {
  const checked = (e.target as HTMLInputElement).checked;

  const existingFilterIndex = userFilters.value.findIndex(filter => filter.key === currentStepCode.value);

  if (existingFilterIndex === -1) {
    userFilters.value.push({
      key: currentStepCode.value,
      filters: checked ? [ value ] : [],
    });
  } else {
    const filters = userFilters.value[existingFilterIndex].filters;
    if (checked && !filters.includes(value)) {
      filters.push(value);
    } else if (!checked) {
      const valueIndex = filters.indexOf(value);
      if (valueIndex !== -1) {
        filters.splice(valueIndex, 1);
      }
    }
  }
};

const reset = () => {
  initalLoading.value = true;
  history.replaceState(
    {},
    '',
    route.path
  );
  showingResult.value = false;
  userFilters.value = [];
  currentStep.value = 0;
  loadFacets();
};

const checkChecked = (code: string) => {
  const existingFilterIndex = userFilters.value.find(filter => filter.key === currentStepCode.value);
  return (existingFilterIndex && existingFilterIndex.filters.includes(code));
};

// * * * * *  STORYBLOK DATA
const getFacetData = (filter: string, facet:string) => {
  if (storyblokSettings.value) {

    const thisFilter = storyblokSettings.value.filters.find(f => f.norceCode === filter);
    if (thisFilter) {
      const thisFacet = thisFilter.facets.find(f => f.norceCode === facet);
      if (thisFacet) {
        return thisFacet;
      }
    }
  }
  return null;
};

// * * * * *  SHOW RESULT
const route = useRoute();
const showingResult = ref(route.query.userFilters !== undefined);

const showResult = () => {
  navigateTo(route.path + '?userFilters=' + encodeURI(JSON.stringify(userFiltersAsUrl.value)));

  if (!props.blok.inline) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  } else {
    const element = document.getElementById('ProductFilterFunnel');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
};

const userFiltersAsUrl = computed(()=> {
  let output = {} as UserFilters;
  userFilters.value.forEach(f => {
    output[f.key] = f.filters;
  });

  if (!output.categoryFilters) {
    output.categoryFilters = [ 'Wood', 'Vinyl' ];
  }
  return output;
});

// * * * * *  SEND RESULT
const { apiPost, handleApiError } = useApiFetch();
const {
  startValidate,
  validateEmail,
} = useValidation();

const showEmailBox = ref(false);
const sendEmail = ref('');
const sendFirstName = ref('');
const sendLastName = ref('');
const sending = ref(false);
const sendDone = ref(false);
const acceptContact = ref(true);

const submitForm = async() => {
  sending.value = true;

  const res = await apiPost('/floor-finder', {
    'email': sendEmail.value,
    'description': window.location.href,
    'firstname': sendFirstName.value,
    'lastname': sendLastName.value,
    'accept_contact': acceptContact.value,
  });

  if (!res) {
    handleApiError();
  } else {
    console.log(res);
  }

  sendDone.value = true;
  sending.value = false;

  setTimeout(()=> {
    sendDone.value = false;
    showEmailBox.value = false;
  }, 2000);

};

const blokForProductList = computed(() => {
  return {
    'adminFilters': [
      {
        '_uid': 'd9f7a4a7-7a9f-4e66-ba45-c35060805b37',
        'link': {
          'id': '',
          'url': '',
          'linktype': 'story',
          'fieldtype': 'multilink',
          'cached_url': '',
        },
        'name': '',
        'image': {
          'id': null,
          'alt': null,
          'name': '',
          'focus': null,
          'title': null,
          'source': null,
          'filename': '',
          'copyright': null,
          'fieldtype': 'asset',
          'meta_data': {},
        },
        'manual': [],
        'linkText': '',
        'component': 'FilterGroupAlgolia',
        'invertText': false,
        'adminFilter': {
          '_uid': '2598da64-5c9f-4169-8a27-cd22b21fb4f2',
          'plugin': 'select-products-filters-algolia',
          'filters': {
            'search': null,
            'filters': userFiltersAsUrl.value,
            'relation': 'AND',
          },
        },
        '_editable': '<!--#storyblok#{"name": "FilterGroupAlgolia", "space": "316553", "uid": "d9f7a4a7-7a9f-4e66-ba45-c35060805b37", "id": "641939687"}-->',
      },
    ],
    '_uid': 'aebf23e5-c71d-4698-bca2-94f4d6919364',
    'body': [],
    'limit': '8',
    'headline': '',
    'subPages': 'off',
    'component': 'ProductList',
    'tabsInline': false,
    'enableFilter': false,
    'limitDesktop': '8',
    'mobileDesign': 'grid',
    'showLoadMore': true,
    'tabsHeadline': '',
    'desktopDesign': 'large',
    'headlineLevel': 'h1',
    'enableQuickBuy': true,
    'subPagesManual': [],
    'showBreadcrumbs': false,
    'visualAppearance': '',
    'logoAboveHeadline': false,
    'favoriteBrandButton': false,
    'scrollToProductsButton': false,
  };
});

</script>

<style scoped lang="postcss">
.inputOverlay {
  background: linear-gradient(180deg, rgba(46, 45, 43, 0.00) 0%, rgba(46, 45, 43, 0.12) 25%, rgba(46, 45, 43, 0.32) 50%, rgba(46, 45, 43, 0.64) 75%, rgba(46, 45, 43, 0.80) 100%);
}
</style>
